import React from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

import { apiKey, apiPrefix, configAgencyName } from "../config";
import { dynamicTextColor, getAcronym } from "../utils/helpers";

const useAxios = (
  agencyId: string
): {
  allRoutes: RouteObj[];
  multiPolylines: MultiPolylineObj[];
  loading: boolean;
} => {
  const { isAuthenticated } = useAuth0();

  const [allRoutes, setAllRoutes] = React.useState<RouteObj[]>([]);
  const [multiPolylines, setMultiPolylines] = React.useState<MultiPolylineObj[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  // Init -> Get Routes Details
  React.useEffect(() => {
    if (!isAuthenticated) return;

    //* Add Acronym if agency is Miami Beach
    const addAcronym = <T extends RouteObj | MultiPolylineObj>(routesObj: T[]): T[] => {
      if (configAgencyName === ("MiamiBeach" as string)) {
        for (let i = 0; i < routesObj.length; i++) {
          routesObj[i].acronym = getAcronym(routesObj[i]?.shortName);
        }
      }
      return routesObj;
    };

    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    // console.log({ CancelToken, source });

    isAuthenticated &&
      setTimeout(async () => {
        await setLoading(true);
        await axios
          .get(`${apiPrefix}/v1/key/${apiKey}/agency/${agencyId}/command/routesDetails`, { cancelToken: source.token })
          .then(async ({ data: { routes: routesList } }) => {
            //* For all agencies but not for SweetWater
            if (configAgencyName !== ("SweetWater" as string)) {
              const routeObjArray = [] as RouteObj[];
              for (let i = 0; i < routesList.length; i++) {
                let routeObj = {
                  id: routesList[i]?.id as string,
                  name: routesList[i]?.name as string,
                  longName: routesList[i]?.longName as string,
                  shortName: routesList[i]?.shortName as string,
                  shapesDirection_0: null,
                  shapesDirection_1: null,
                  background: routesList[i]?.color ? (`#${routesList[i]?.color}` as string) : "#000000",
                  color: routesList[i]?.color
                    ? dynamicTextColor(`#${routesList[i]?.color}` as string)
                    : dynamicTextColor("#000000"),
                };

                if (routesList[i]?.shape?.length >= 1) {
                  const shapesDirection_0 = routesList[i]?.shape.filter(
                    (elem: { directionId: string }) => elem?.directionId === "0"
                  );
                  const shapesDirection_1 = routesList[i]?.shape.filter(
                    (elem: { directionId: string }) => elem?.directionId === "1"
                  );
                  routeObj = { ...routeObj, shapesDirection_0, shapesDirection_1 };
                }

                for (let i = 0; i < routeObjArray?.length; i++) {
                  if (routeObjArray[i].shapesDirection_0 && routeObjArray[i].shapesDirection_0?.length) {
                    for (let j = 0; j < routeObjArray[i].shapesDirection_0!.length; j++) {
                      for (let k = 0; k < routeObjArray[i].shapesDirection_0![j].loc.length; k++) {
                        routeObjArray[i].shapesDirection_0![j].loc[k].lng =
                          routeObjArray[i].shapesDirection_0![j].loc[k].lon;
                        //* Deletes also routeObjArray[i].shapesDirection_0![j].loc[k].lng;
                        // delete routeObjArray[i].shapesDirection_0![j].loc[k].lon;
                      }
                    }
                  }
                }
                for (let i = 0; i < routeObjArray?.length; i++) {
                  if (routeObjArray[i].shapesDirection_1 && routeObjArray[i].shapesDirection_1?.length) {
                    for (let j = 0; j < routeObjArray[i].shapesDirection_1!.length; j++) {
                      for (let k = 0; k < routeObjArray[i].shapesDirection_1![j].loc.length; k++) {
                        routeObjArray[i].shapesDirection_1![j].loc[k].lng =
                          routeObjArray[i].shapesDirection_1![j].loc[k].lon;
                        //* Deletes also routeObjArray[i].shapesDirection_1![j].loc[k].lng;
                        // delete routeObjArray[i].shapesDirection_1![j].loc[k].lon;
                      }
                    }
                  }
                }
                routeObjArray.push(routeObj);
              }

              //* Add Acronym if agency is Miami Beach
              const routesObjArrToSet: RouteObj[] =
                configAgencyName === ("MiamiBeach" as string) ? addAcronym(routeObjArray) : [...routeObjArray];
              await setAllRoutes(routesObjArrToSet);

              //- Turf polylines -> array of polylines for each route!
              const polylinesArr = [] as MultiPolylineObj[];
              for (let i = 0; i < routesList?.length; i++) {
                const polylineObj = {
                  id: routesList[i]?.id as string,
                  name: routesList[i]?.name as string,
                  longName: routesList[i]?.longName as string,
                  shortName: routesList[i]?.shortName as string,
                  shapes: routesList[i]?.shape
                    ?.map((shape: { loc: { lon: number; lat: number }[][] }) => shape?.loc)
                    ?.filter((path: { lon: number; lat: number }[]) => path?.length >= 6)
                    ?.map((route: { lon: number; lat: number }[]) =>
                      route?.map((point: { lon: number; lat: number }) => {
                        return [point?.lon, point?.lat];
                      })
                    ) as Line[],
                };
                polylinesArr.push(polylineObj);
              }

              //* Add Acronym if agency is Miami Beach
              const polylinesArrToSet: MultiPolylineObj[] =
                configAgencyName === ("MiamiBeach" as string) ? addAcronym(polylinesArr) : [...polylinesArr];
              await setMultiPolylines(polylinesArrToSet);
            }

            //* For Sweetwater only
            // Todo: No longer needed
            if (configAgencyName === ("SweetWater" as string)) {
              // console.log("SweetWater");
              let routeObj = {
                id: routesList[0]?.id as string,
                name: routesList[0]?.name as string,
                longName: routesList[0]?.longName as string,
                shortName: routesList[0]?.shortName as string,
                shapesDirection_0: routesList[0]?.shape,
                shapesDirection_1: null,
                background: `#${routesList[0]?.color}` as string,
                color: dynamicTextColor(`#${routesList[0]?.color}` as string),
              };
              setAllRoutes([routeObj]);
            }
          })
          .catch((error) => {
            if (error.response) {
              console.error("error.response.status:", error.response.status);
            }
            if (axios.isCancel(error)) {
              console.log("Request canceled", error.message);
            }
          });
        setLoading(false);
      }, 1000);

    return () => {
      source.cancel();
    };
  }, [agencyId, isAuthenticated]);

  return { allRoutes, multiPolylines, loading };
};

export default useAxios;
